import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "../lib/telemetry";
import { Button } from "./Button";

interface State { error: Error | null }

/**
 * Catches a render error on one screen and shows a way out instead of a blank page. The error is logged to the
 * console for debugging; the message shown is the design's plain sentence (what happened, what is safe, what to do).
 */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string }, State> {
  override state: State = { error: null };
  static getDerivedStateFromError(error: Error): State { return { error }; }
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[digu] screen failed", error, info.componentStack);
    // The component stack names the screen that broke; the JS stack where (D-077).
    const withScreens = Object.assign(new Error(error.message), { name: error.name, stack: `${error.stack ?? ""}\n--- components${info.componentStack ?? ""}`.slice(0, 2000) });
    reportError("boundary", withScreens);
  }
  override componentDidUpdate(prev: { resetKey?: string }): void { if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null }); }
  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="screen-fallback" role="alert" style={{ padding: "48px 16px", textAlign: "center" }}>
        <h1 className="d-m" style={{ marginBottom: 8 }}>This screen hit a snag.</h1>
        <p className="body" style={{ color: "var(--color-text-secondary)", marginBottom: 24 }}>Nothing was lost. Reload to try again, or go back home.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "stretch", maxWidth: 320, margin: "0 auto" }}>
          <Button onClick={() => window.location.reload()}>Reload</Button>
          <Button kind="quiet" onClick={() => window.location.assign("/")}>Back home</Button>
        </div>
      </div>
    );
  }
}
